import { ChevronRight, ArrowRightLeft } from 'lucide-react';
import { transactions as mockTransactions } from '@/data/blockchainData';
import { TronSocketData } from '@/hooks/useTronSocket';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatTimeAgo } from '@/lib/timeUtils';

interface TransactionsListProps {
  socketData?: TronSocketData;
}

const TransactionsList = ({ socketData }: TransactionsListProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const transactions = useMemo(() => {
    if (socketData?.latestTransactions && socketData.latestTransactions.length > 0) {
      return socketData.latestTransactions.map(tx => ({
        hash: tx.hash,
        hashDisplay: tx.hash.length > 16 ? tx.hash.slice(0, 8) + '...' + tx.hash.slice(-5) : tx.hash,
        from: tx.from,
        fromDisplay: tx.from.length > 16 ? tx.from.slice(0, 8) + '...' + tx.from.slice(-5) : tx.from,
        to: tx.to,
        toDisplay: tx.to.length > 16 ? tx.to.slice(0, 8) + '...' + tx.to.slice(-5) : tx.to,
        amount: tx.amount,
        token: tx.tokenName,
        type: tx.type,
        timestamp: new Date(tx.timestamp),
      }));
    }
    return mockTransactions.map(tx => ({
      ...tx,
      hashDisplay: tx.hash,
      fromDisplay: tx.from,
      toDisplay: tx.to,
    }));
  }, [socketData?.latestTransactions]);

  const handleTransactionClick = (hash: string) => {
    navigate(`/search?q=${hash}&type=transaction`);
  };

  const handleAddressClick = (address: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/search?q=${address}&type=account`);
  };

  return (
    <div className="glass-card overflow-visible">
      <div className="flex items-center p-5 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-chart-3/10 rounded-xl">
            <ArrowRightLeft className="w-5 h-5 text-chart-3" />
          </div>
          <h2 className="text-lg font-bold text-foreground">{t('transactions.title')}</h2>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[500px]">
          <tbody>
            {transactions.slice(0, 6).map((tx, index) => (
              <tr 
                key={`${tx.hash}-${index}`} 
                className="border-b border-border/30 hover:bg-accent/30 cursor-pointer transition-all duration-200 animate-fade-in"
                onClick={() => handleTransactionClick(tx.hash)}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <td className="p-4">
                  <div>
                    <p className="font-mono text-sm text-primary font-medium hover:underline">{tx.hashDisplay}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatTimeAgo(tx.timestamp, t)}
                    </p>
                  </div>
                </td>
                <td className="p-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-10">{t('transactions.from')}</span>
                      <span 
                        className="font-mono text-sm text-foreground hover:text-primary cursor-pointer transition-colors"
                        onClick={(e) => handleAddressClick(tx.from, e)}
                      >
                        {tx.fromDisplay}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-10">{t('transactions.to')}</span>
                      <span 
                        className="font-mono text-sm text-foreground hover:text-primary cursor-pointer transition-colors"
                        onClick={(e) => handleAddressClick(tx.to, e)}
                      >
                        {tx.toDisplay}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <p className="font-semibold text-foreground">
                    {tx.amount.toLocaleString(undefined, { maximumFractionDigits: tx.token === 'TRX' ? 0 : 6 })} 
                    <span className="text-primary ml-1">{tx.token}</span>
                  </p>
                  <span className="badge-transfer mt-1 inline-block">{tx.type}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransactionsList;
